/**
 * Tag URL helpers, shared by the tag route, the blog cards and the sitemap.
 *
 * Lives in its own module (no Prisma import) so client components can link to a
 * tag without pulling the database layer into the browser bundle.
 */

/**
 * URL form of a CMS tag.
 *
 * Tags are free text typed by editors, so they arrive with inconsistent case
 * and spacing ("CRM", "crm", "Line OA"). Lower-casing and hyphenating collapses
 * those into one canonical URL. Thai tags have no case and no spaces to fold,
 * so they pass through and get percent-encoded by the router — which is fine
 * for crawlers, and keeps the mapping loss-free rather than transliterating.
 */
export function tagSlug(tag: string): string {
  return (
    tag
      .trim()
      .toLowerCase()
      // Editors write some tags hashtag-style ("#เปิดร้านอาหาร"). Those
      // characters are either reserved in a URL path or survive only as ugly
      // percent-escapes, so drop them — "#crm" and "crm" are the same topic and
      // deliberately fold into one page.
      .replace(/[#?&/\\%+]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
  );
}

/**
 * Fewest published articles a tag needs before its page is indexed and listed
 * in the sitemap. A tag on one article is a page that repeats a single card —
 * thin content competing with the article itself — so it stays reachable for
 * readers but out of the index.
 */
export const MIN_INDEXED_TAG_POSTS = 2;

/** Path to a tag's listing page. */
export function tagPath(tag: string): string {
  return `/blog/tag/${encodeURIComponent(tagSlug(tag))}`;
}
